// USER-ROLE-MGMT-UI-01
// TreatX Admin surface to manage role_assignments[] on EXISTING canonical
// rbac_users rows. This is a thin operational wrapper around the pre-existing
// backend authority:
//   GET    /api/admin/users?search=&limit=&offset=
//   GET    /api/admin/users/{user_id}
//   POST   /api/admin/users/{user_id}/roles          body: {role_id, clinic_id?}
//   DELETE /api/admin/users/{user_id}/roles/{role_id}
//
// The frontend never sends granted_by / actor identity — the backend derives
// the authenticated actor from the session cookie. RBAC (require_permission
// "user.manage") is enforced server-side; this page only gates access via
// RBACProtectedRoute so that unauthorized roles never see the surface.
//
// Elevated / platform roles (treatx_super_admin, treatx_admin, etc.) show
// a confirmation dialog before grant/remove. No new maker-checker workflow
// is introduced — role assignment continues to be a single-actor operation
// audited by role_assigned events.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RefreshCw, Search, ShieldAlert, Trash2, UserPlus } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
// B-2.1 — Owner-mandated canonical DD/MM/YYYY HH:mm:ss (24-hour)
// presentation for user-facing timestamps. Never call `toLocaleString`
// / `toLocaleDateString` directly in this surface.
import { formatDateLocale, formatDateTimeLocale } from '../../utils/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Canonical role vocabulary — must match backend rbac_service.StandardRole values.
// Order used only for display; presentation only, not authority.
const ASSIGNABLE_ROLES = [
  'patient',
  'provider_admin',
  'provider_sales_rep',
  'provider_coordinator',
  'provider_accountant',
  'provider_after_sales_rep',
  'provider_doctor',
  'provider_staff',
  'treatx_support',
  'treatx_sales_rep',
  'treatx_coordinator',
  'treatx_accountant',
  'treatx_after_sales_rep',
  'treatx_admin',
  'treatx_super_admin',
];

const CLINIC_SCOPED_ROLES = new Set([
  'provider_admin',
  'provider_sales_rep',
  'provider_coordinator',
  'provider_accountant',
  'provider_after_sales_rep',
  'provider_doctor',
  'provider_staff',
]);

// Platform/high-privilege roles that trigger a confirmation dialog.
const ELEVATED_ROLES = new Set([
  'treatx_admin',
  'treatx_super_admin',
  'treatx_accountant',
]);

async function apiFetch(path, opts = {}) {
  const url = API ? `${API}${path}` : path;
  const headers = { ...(opts.headers || {}) };
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { ...opts, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON */ }
  return { ok: res.ok, status: res.status, body };
}

export default function UserRoleManagement() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [roleHistory, setRoleHistory] = useState([]);
  const [roleToAdd, setRoleToAdd] = useState('');
  const [clinicIdForRole, setClinicIdForRole] = useState('');
  const [busy, setBusy] = useState(false);

  // TREATX-OWNER-UAT-USER-PAGINATION-01: canonical server-side pagination
  // over /api/admin/users?limit=&offset=&search=. Total is returned by
  // the backend. Do NOT paginate in the browser.
  const [page, setPage] = useState(0);
  // TREATX-OWNER-UAT-USER-PAGE-SIZE-50 (2026-06, Owner request): show 50
  // users per page instead of 25. Server-side pagination, search, filters
  // and sorting are unchanged — only the page window grew. The canonical
  // GET /api/admin/users accepts limit=50 and returns `total` for the
  // pager, so pages beyond the first 50 remain reachable.
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // P1-USER-VISIBILITY-01 — the filter vocabularies are the OBSERVED values
  // reported by the canonical `GET /api/admin/users` facets, not a hardcoded
  // list. A legacy role (`doctor`, `clinic_admin`) or a row with no `status`
  // can therefore never become unreachable through the filters.
  const [facets, setFacets] = useState({ roles: [], statuses: [], without_role_assignment: 0 });

  const [confirm, setConfirm] = useState({ open: false, kind: null, roleId: null, clinicId: null });

  const roleLabel = (roleId) =>
    t(`treatxAdmin.userRoles.roles.${roleId}`, { defaultValue: roleId });

  const runSearch = useCallback(async (raw, pageArg = 0) => {
    setSearching(true);
    try {
      const q = (raw ?? search).trim();
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      if (roleFilter) params.set('role', roleFilter);
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', String(pageSize));
      params.set('offset', String(pageArg * pageSize));
      const { ok, body } = await apiFetch(`/api/admin/users?${params.toString()}`);
      if (!ok || !body) {
        toast.error(t('treatxAdmin.userRoles.searchFailed'));
        setUsers([]);
        setTotal(0);
      } else {
        setUsers(Array.isArray(body.users) ? body.users : []);
        setTotal(typeof body.total === 'number' ? body.total : 0);
        if (body.facets) {
          setFacets({
            roles: Array.isArray(body.facets.roles) ? body.facets.roles : [],
            statuses: Array.isArray(body.facets.statuses) ? body.facets.statuses : [],
            without_role_assignment: body.facets.without_role_assignment || 0,
          });
        }
        setPage(pageArg);
      }
    } finally {
      setSearching(false);
    }
  }, [search, roleFilter, statusFilter, pageSize, t]);

  const loadUser = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingUser(true);
    setRoleHistory([]);
    try {
      const [detailRes, historyRes] = await Promise.all([
        apiFetch(`/api/admin/users/${encodeURIComponent(userId)}`),
        apiFetch(`/api/admin/users/${encodeURIComponent(userId)}/role-history?limit=100`),
      ]);
      if (!detailRes.ok || !detailRes.body?.user) {
        toast.error(t('treatxAdmin.userRoles.loadFailed'));
        setSelectedUser(null);
      } else {
        setSelectedUser(detailRes.body.user);
      }
      if (historyRes.ok && Array.isArray(historyRes.body?.events)) {
        setRoleHistory(historyRes.body.events);
      } else {
        setRoleHistory([]);
      }
    } finally {
      setLoadingUser(false);
    }
  }, [t]);

  useEffect(() => { runSearch('', 0); }, [runSearch]);

  const doAddRole = useCallback(async (roleId, clinicId) => {
    if (!selectedUser?.user_id || !roleId) return;
    setBusy(true);
    try {
      const payload = { role_id: roleId };
      if (clinicId) payload.clinic_id = clinicId;
      const { ok, status, body } = await apiFetch(
        `/api/admin/users/${encodeURIComponent(selectedUser.user_id)}/roles`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
      if (ok) {
        toast.success(t('treatxAdmin.userRoles.grantSuccess', { role: roleLabel(roleId) }));
        await loadUser(selectedUser.user_id);
        setRoleToAdd('');
        setClinicIdForRole('');
      } else if (status === 409) {
        toast.error(t('treatxAdmin.userRoles.grantDuplicate'));
      } else if (status === 403) {
        toast.error(t('treatxAdmin.userRoles.grantForbidden'));
      } else {
        toast.error(body?.detail || t('treatxAdmin.userRoles.grantFailed'));
      }
    } finally {
      setBusy(false);
    }
  }, [selectedUser, t, loadUser]);

  const doRemoveRole = useCallback(async (roleId, clinicId) => {
    if (!selectedUser?.user_id || !roleId) return;
    setBusy(true);
    try {
      const qs = clinicId ? `?clinic_id=${encodeURIComponent(clinicId)}` : '';
      const { ok, status, body } = await apiFetch(
        `/api/admin/users/${encodeURIComponent(selectedUser.user_id)}/roles/${encodeURIComponent(roleId)}${qs}`,
        { method: 'DELETE' },
      );
      if (ok) {
        toast.success(t('treatxAdmin.userRoles.revokeSuccess', { role: roleLabel(roleId) }));
        await loadUser(selectedUser.user_id);
      } else if (status === 403) {
        toast.error(t('treatxAdmin.userRoles.grantForbidden'));
      } else {
        toast.error(body?.detail || t('treatxAdmin.userRoles.revokeFailed'));
      }
    } finally {
      setBusy(false);
    }
  }, [selectedUser, t, loadUser]);

  const requestAdd = () => {
    if (!roleToAdd) return;
    if (CLINIC_SCOPED_ROLES.has(roleToAdd) && !clinicIdForRole.trim()) {
      toast.error(t('treatxAdmin.userRoles.clinicIdRequired'));
      return;
    }
    if (ELEVATED_ROLES.has(roleToAdd)) {
      setConfirm({ open: true, kind: 'add', roleId: roleToAdd, clinicId: clinicIdForRole.trim() || null });
    } else {
      doAddRole(roleToAdd, clinicIdForRole.trim() || null);
    }
  };

  const requestRemove = (roleId, clinicId) => {
    if (ELEVATED_ROLES.has(roleId)) {
      setConfirm({ open: true, kind: 'remove', roleId, clinicId: clinicId || null });
    } else {
      doRemoveRole(roleId, clinicId || null);
    }
  };

  const confirmAction = () => {
    const { kind, roleId, clinicId } = confirm;
    setConfirm({ open: false, kind: null, roleId: null, clinicId: null });
    if (kind === 'add') doAddRole(roleId, clinicId);
    else if (kind === 'remove') doRemoveRole(roleId, clinicId);
  };

  const currentAssignments = useMemo(
    () => Array.isArray(selectedUser?.role_assignments) ? selectedUser.role_assignments : [],
    [selectedUser],
  );

  const availableRoles = useMemo(() => {
    const present = new Set(currentAssignments.map((ra) => `${ra.role_id}::${ra.scope?.id || ''}`));
    return ASSIGNABLE_ROLES.filter((r) => {
      // Hide from the picker if a global-scoped copy already exists.
      const globalKey = `${r}::`;
      return !present.has(globalKey);
    });
  }, [currentAssignments]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate('/treatx-admin/dashboard')}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2"
              data-testid="user-roles-back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('treatxAdmin.userRoles.backToDashboard')}
            </button>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="user-roles-title">
              {t('treatxAdmin.userRoles.title')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t('treatxAdmin.userRoles.subtitle')}</p>
          </div>
          <Button variant="outline" onClick={() => runSearch(undefined, page)} disabled={searching} data-testid="user-roles-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('treatxAdmin.userRoles.refresh')}
          </Button>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base">{t('treatxAdmin.userRoles.searchTitle')}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('treatxAdmin.userRoles.searchPlaceholder')}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(undefined, 0); }}
                  data-testid="user-roles-search-input"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); }}
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="user-roles-role-filter"
              >
                <option value="">{t('treatxAdmin.userRoles.anyRole')}</option>
                {facets.roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {t('treatxAdmin.userRoles.facetCount', { value: roleLabel(r.value), count: r.count })}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); }}
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="user-roles-status-filter"
              >
                <option value="">{t('treatxAdmin.userRoles.anyStatus')}</option>
                {facets.statuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {t('treatxAdmin.userRoles.facetCount', {
                      value: s.unset ? t('treatxAdmin.userRoles.statusUnset') : s.value,
                      count: s.count,
                    })}
                  </option>
                ))}
              </select>
              <Button onClick={() => runSearch(undefined, 0)} disabled={searching} data-testid="user-roles-search-btn">
                <Search className="h-4 w-4 mr-2" />
                {t('treatxAdmin.userRoles.searchAction')}
              </Button>
            </div>

            {roleFilter && facets.without_role_assignment > 0 && (
              <p className="mt-2 text-xs text-amber-700" data-testid="user-roles-without-role-notice">
                {t('treatxAdmin.userRoles.withoutRole', { count: facets.without_role_assignment })}
              </p>
            )}

            <div className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="user-roles-search-results">
              {searching && (
                <div className="p-3 text-sm text-slate-500">{t('treatxAdmin.userRoles.searching')}</div>
              )}
              {!searching && users.length === 0 && (
                <div className="p-3 text-sm text-slate-500">{t('treatxAdmin.userRoles.noResults')}</div>
              )}
              {users.map((u) => {
                const isSelected = u.user_id === selectedUserId;
                return (
                  <button
                    key={u.user_id}
                    onClick={() => { setSelectedUserId(u.user_id); loadUser(u.user_id); }}
                    className={`w-full text-left p-3 hover:bg-slate-50 flex items-center justify-between ${isSelected ? 'bg-slate-100' : ''}`}
                    data-testid={`user-roles-result-${u.user_id}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900">{u.name || u.email}</div>
                      <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {(u.role_assignments || []).slice(0, 4).map((ra, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {roleLabel(ra.role_id)}
                        </Badge>
                      ))}
                      {(u.role_assignments || []).length > 4 && (
                        <Badge variant="outline" className="text-xs">+{(u.role_assignments || []).length - 4}</Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* TREATX-OWNER-UAT-USER-PAGINATION-01 — server-side pager */}
            {total > 0 && (
              <div className="mt-3 flex items-center justify-between text-xs text-slate-600" data-testid="user-roles-pager">
                <div>
                  Showing <strong>{page * pageSize + 1}</strong>–<strong>{Math.min(total, (page + 1) * pageSize)}</strong> of <strong>{total}</strong> users
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={page === 0 || searching} onClick={() => runSearch(undefined, page - 1)} data-testid="user-roles-page-prev">
                    Previous
                  </Button>
                  <span className="text-slate-500">Page {page + 1} / {Math.max(1, Math.ceil(total / pageSize))}</span>
                  <Button size="sm" variant="outline" disabled={(page + 1) * pageSize >= total || searching} onClick={() => runSearch(undefined, page + 1)} data-testid="user-roles-page-next">
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected user detail */}
        {selectedUserId && (
          <Card data-testid="user-roles-detail-card">
            <CardHeader>
              <CardTitle className="text-base">{t('treatxAdmin.userRoles.detailTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingUser && (
                <div className="text-sm text-slate-500">{t('treatxAdmin.userRoles.loadingUser')}</div>
              )}
              {!loadingUser && selectedUser && (
                <div className="space-y-6">
                  {/* Identity — ACCOUNT */}
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                      {t('treatxAdmin.userRoles.accountSection')}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-slate-500">{t('treatxAdmin.userRoles.email')}</div>
                        <div className="font-mono text-slate-900 flex items-center gap-1" data-testid="user-roles-detail-email">
                          {selectedUser.email}
                          {selectedUser.email_verified && (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5" data-testid="user-roles-detail-email-verified">verified</span>
                          )}
                        </div>
                        {selectedUser.email_verified_at && (
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            verified: {formatDateTimeLocale(selectedUser.email_verified_at)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">{t('treatxAdmin.userRoles.name')}</div>
                        <div className="text-slate-900">{selectedUser.name || '—'}</div>
                        {(selectedUser.given_name || selectedUser.family_name) && (
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {selectedUser.given_name || ''} {selectedUser.family_name || ''}
                          </div>
                        )}
                      </div>
                      {selectedUser.phone && (
                        <div>
                          <div className="text-xs text-slate-500">Phone</div>
                          <div className="text-slate-900 font-mono" data-testid="user-roles-detail-phone">{selectedUser.phone}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-xs text-slate-500">{t('treatxAdmin.userRoles.status')}</div>
                        <div className="text-slate-900">{selectedUser.status || '—'}</div>
                      </div>
                      {/* TREATX-OWNER-UAT-USER-DETAIL-01 — authentication method + last successful login. */}
                      <div>
                        <div className="text-xs text-slate-500">Primary authentication</div>
                        <div className="text-slate-900 flex flex-wrap gap-1" data-testid="user-roles-detail-auth-providers">
                          {(selectedUser.auth_providers || []).length === 0
                            ? <span className="text-slate-400">— (password only)</span>
                            : (selectedUser.auth_providers || []).map((p) => (
                                <span key={p} className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[10px] px-2 py-0.5 border">
                                  {p === 'email_password' ? 'password' : p}
                                </span>
                              ))}
                        </div>
                      </div>
                      {/* TREATX-OWNER-UAT-USER-DETAIL-02 (2026-08) — Multi-factor
                          authentication summary sourced from the canonical
                          `rbac_users.mfa` sub-document (safe-projected server-side
                          as `mfa_summary`). No parallel MFA authority. */}
                      <div>
                        <div className="text-xs text-slate-500">Multi-factor authentication</div>
                        <div className="text-slate-900" data-testid="user-roles-detail-mfa">
                          {(() => {
                            const s = selectedUser.mfa_summary || {};
                            const status = s.status || 'not_enrolled';
                            const method = (s.method || 'totp').toUpperCase();
                            if (status === 'enabled') {
                              return (
                                <span className="inline-flex items-center gap-1">
                                  <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[10px] px-2 py-0.5 border border-emerald-200">
                                    {method}
                                  </span>
                                  <span className="text-emerald-700 text-xs">· Enabled</span>
                                  {s.enabled_at && (
                                    <span className="text-slate-400 text-[10px]">
                                      · since {formatDateLocale(s.enabled_at)}
                                    </span>
                                  )}
                                </span>
                              );
                            }
                            if (status === 'enrollment_pending') {
                              return <span className="text-amber-700 text-xs">Enrollment pending</span>;
                            }
                            if (status === 'disabled') {
                              return <span className="text-slate-500 text-xs">Disabled</span>;
                            }
                            return <span className="text-slate-400 text-xs">Not enrolled</span>;
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Last successful login</div>
                        <div className="text-slate-900" data-testid="user-roles-detail-last-login">
                          {selectedUser.last_login_at
                            ? formatDateTimeLocale(selectedUser.last_login_at)
                            : <span className="text-slate-400">— (never)</span>}
                        </div>
                      </div>
                      {selectedUser.created_at && (
                        <div>
                          <div className="text-xs text-slate-500">{t('treatxAdmin.userRoles.joined')}</div>
                          <div className="text-slate-900" data-testid="user-roles-detail-joined">
                            {formatDateTimeLocale(selectedUser.created_at)}
                          </div>
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <div className="text-[10px] text-slate-400">{t('treatxAdmin.userRoles.userId')}</div>
                        <div className="font-mono text-[11px] text-slate-500 truncate" data-testid="user-roles-detail-user-id">{selectedUser.user_id}</div>
                      </div>
                    </div>
                  </div>

                  {/* Current roles */}
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t('treatxAdmin.userRoles.currentRoles')}</div>
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="user-roles-current-roles">
                      {currentAssignments.length === 0 && (
                        <div className="p-3 text-sm text-slate-500">{t('treatxAdmin.userRoles.noRoles')}</div>
                      )}
                      {currentAssignments.map((ra, i) => {
                        const isElevated = ELEVATED_ROLES.has(ra.role_id);
                        return (
                          <div key={i} className="p-3 flex items-center justify-between gap-2" data-testid={`user-roles-row-${ra.role_id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center flex-wrap gap-2">
                                <Badge variant={isElevated ? 'default' : 'secondary'}>
                                  {roleLabel(ra.role_id)}
                                </Badge>
                                {ra.scope?.type === 'clinic' && (
                                  <span className="text-xs text-slate-500 font-mono">
                                    {t('treatxAdmin.userRoles.scopeClinic')}: {ra.scope.id}
                                  </span>
                                )}
                                {ra.scope?.type === 'global' && (
                                  <span className="text-xs text-slate-500">
                                    {t('treatxAdmin.userRoles.scopeGlobal')}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                                {ra.granted_at && (
                                  <span>
                                    {t('treatxAdmin.userRoles.granted')}:{' '}
                                    <span className="text-slate-700">{formatDateTimeLocale(ra.granted_at)}</span>
                                  </span>
                                )}
                                {(ra.granted_by_name || ra.granted_by) && (
                                  <span>
                                    {t('treatxAdmin.userRoles.grantedBy')}:{' '}
                                    <span className="text-slate-700">{ra.granted_by_name || ra.granted_by}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => requestRemove(ra.role_id, ra.scope?.type === 'clinic' ? ra.scope.id : null)}
                              disabled={busy}
                              data-testid={`user-roles-remove-${ra.role_id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              {t('treatxAdmin.userRoles.remove')}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Role History (read-only, canonical rbac_audit_log) */}
                  {roleHistory.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                        {t('treatxAdmin.userRoles.roleHistoryTitle')}
                      </div>
                      <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="user-roles-history">
                        {roleHistory.map((e, i) => {
                          const isGrant = e.action_type === 'role_assigned';
                          const roleId = e.metadata?.role_id || e.role_id;
                          const scope = e.metadata?.scope || e.scope;
                          return (
                            <div key={i} className="p-3 flex items-start justify-between gap-3 text-sm"
                                 data-testid={`user-roles-history-${i}`}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center flex-wrap gap-2">
                                  <Badge variant={isGrant ? 'default' : 'secondary'} className="text-[10px]">
                                    {t(isGrant ? 'treatxAdmin.userRoles.eventGranted' : 'treatxAdmin.userRoles.eventRemoved')}
                                  </Badge>
                                  <span className="text-slate-800">{roleId ? roleLabel(roleId) : '—'}</span>
                                  {scope?.type === 'clinic' && scope?.id && (
                                    <span className="text-xs text-slate-500 font-mono">
                                      {t('treatxAdmin.userRoles.scopeClinic')}: {scope.id}
                                    </span>
                                  )}
                                  {scope?.type === 'global' && (
                                    <span className="text-xs text-slate-500">
                                      {t('treatxAdmin.userRoles.scopeGlobal')}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                                  {e.timestamp && (
                                    <span className="text-slate-700">
                                      {formatDateTimeLocale(e.timestamp)}
                                    </span>
                                  )}
                                  {(e.actor_name || e.actor_user_id) && (
                                    <span>
                                      {t('treatxAdmin.userRoles.grantedBy')}:{' '}
                                      <span className="text-slate-700">{e.actor_name || e.actor_user_id}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Add role */}
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t('treatxAdmin.userRoles.addRoleTitle')}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-1">
                        <Label className="text-xs text-slate-500">{t('treatxAdmin.userRoles.roleLabel')}</Label>
                        <Select value={roleToAdd} onValueChange={setRoleToAdd}>
                          <SelectTrigger data-testid="user-roles-add-role-select">
                            <SelectValue placeholder={t('treatxAdmin.userRoles.selectRole')} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoles.map((r) => (
                              <SelectItem key={r} value={r} data-testid={`user-roles-role-option-${r}`}>
                                {roleLabel(r)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {CLINIC_SCOPED_ROLES.has(roleToAdd) && (
                        <div className="sm:col-span-1">
                          <Label className="text-xs text-slate-500">{t('treatxAdmin.userRoles.clinicIdLabel')}</Label>
                          <Input
                            value={clinicIdForRole}
                            onChange={(e) => setClinicIdForRole(e.target.value)}
                            placeholder={t('treatxAdmin.userRoles.clinicIdPlaceholder')}
                            data-testid="user-roles-clinic-id-input"
                          />
                        </div>
                      )}
                      <div className="sm:col-span-1 flex items-end">
                        <Button onClick={requestAdd} disabled={busy || !roleToAdd} data-testid="user-roles-add-btn">
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t('treatxAdmin.userRoles.addRoleAction')}
                        </Button>
                      </div>
                    </div>
                    {roleToAdd && ELEVATED_ROLES.has(roleToAdd) && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="user-roles-elevated-notice">
                        <ShieldAlert className="h-4 w-4 mt-0.5 flex-none" />
                        <div>{t('treatxAdmin.userRoles.elevatedNotice', { role: roleLabel(roleToAdd) })}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation dialog for elevated role grant/remove */}
      <Dialog open={confirm.open} onOpenChange={(o) => { if (!o) setConfirm({ open: false, kind: null, roleId: null, clinicId: null }); }}>
        <DialogContent data-testid="user-roles-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm.kind === 'remove'
                ? t('treatxAdmin.userRoles.confirmRemoveTitle')
                : t('treatxAdmin.userRoles.confirmAddTitle')}
            </DialogTitle>
            <DialogDescription>
              {confirm.kind === 'remove'
                ? t('treatxAdmin.userRoles.confirmRemoveBody', {
                    role: confirm.roleId ? roleLabel(confirm.roleId) : '',
                    email: selectedUser?.email || '',
                  })
                : t('treatxAdmin.userRoles.confirmAddBody', {
                    role: confirm.roleId ? roleLabel(confirm.roleId) : '',
                    email: selectedUser?.email || '',
                  })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirm({ open: false, kind: null, roleId: null, clinicId: null })}
              data-testid="user-roles-confirm-cancel"
            >
              {t('treatxAdmin.userRoles.confirmCancel')}
            </Button>
            <Button onClick={confirmAction} disabled={busy} data-testid="user-roles-confirm-ok">
              {confirm.kind === 'remove'
                ? t('treatxAdmin.userRoles.confirmRemoveAction')
                : t('treatxAdmin.userRoles.confirmAddAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
